import type { ParkingSpotAttributes } from '@lotngo/shared';
import { useTranslation } from '../../i18n';

interface Props {
  attributes?: ParkingSpotAttributes;
  /** Optional — when a value here matches the spot's own value for that
   * attribute, the chip renders highlighted (used by FindSpotPage to show
   * why a spot matched the active attribute filters). */
  activeFilters?: Partial<Record<keyof ParkingSpotAttributes, string[]>>;
}

interface ChipDef {
  key:      keyof ParkingSpotAttributes;
  shortKey: string;  // translation key for the short property label ("Size", "Tandem"...)
  prefix:   string;  // value-translation prefix ("vehicle_", "dependency_"...)
  icon?:    string;
}

const CHIPS: ChipDef[] = [
  { key: 'structure_type',    shortKey: 'attr_chip_structure',  prefix: 'structure_',  icon: 'directions_car' },
  { key: 'vehicle_size',      shortKey: 'attr_chip_vehicle',    prefix: 'vehicle_',    icon: 'straighten' },
  { key: 'dependency_status', shortKey: 'attr_chip_dependency', prefix: 'dependency_', icon: 'link' },
];

/**
 * Renders one chip per non-'unknown' attribute as "ShortLabel: Value".
 * Hides itself entirely when every attribute is 'unknown' or `attributes`
 * is undefined (old API rows that didn't include attributes yet).
 */
export function AttributeChips({ attributes, activeFilters }: Props) {
  const { t } = useTranslation();
  if (!attributes) return null;

  const chips = CHIPS
    .map((c) => ({ ...c, value: attributes[c.key] }))
    .filter((c) => c.value && c.value !== 'unknown');

  if (chips.length === 0) return null;

  return (
    <div
      style={{
        display:  'flex',
        flexWrap: 'wrap',
        gap:      '6px',
        marginTop: '8px',
      }}
    >
      {chips.map(({ key, shortKey, prefix, icon, value }) => {
        const isActive = activeFilters?.[key]?.includes(value) ?? false;
        return (
          <span
            key={key}
            style={{
              display:      'inline-flex',
              alignItems:   'center',
              gap:          '4px',
              padding:      '3px 8px',
              borderRadius: '999px',
              background:   isActive ? '#dde8ff' : '#f1f5f9',
              color:        isActive ? '#003580' : '#0f172a',
              fontFamily:   "'Manrope', system-ui, sans-serif",
              fontSize:     '11px',
              fontWeight:   600,
              lineHeight:   1.2,
              border:       isActive ? '1px solid #005bc0' : '1px solid #e2e8f0',
            }}
          >
            {icon && (
              <span
                className="material-symbols-outlined"
                style={{ fontSize: '13px', lineHeight: 1 }}
                aria-hidden
              >
                {icon}
              </span>
            )}
            <span style={{ fontWeight: 700 }}>{t(shortKey)}:</span>
            <span style={{ fontWeight: 500 }}>{t(`${prefix}${value}`)}</span>
          </span>
        );
      })}
    </div>
  );
}
